import { memo, useEffect, useRef, useState } from "react";
import Popover from "../popover/popover";
import SettingsIcon from "./settingsIcon";
import { useAppDispatch, useAppSelector } from "redux-share/hooks";
import { setClearPopoversSlice, setSettingPopover, setSharePopover } from "redux-share/slices/popoversSlice";
import useOnClickOutside from "helpers/hooks/useOnClickOutside";
import { conditions, justAudio } from "helpers";
import { ConditionName } from "interfaces";
import { setUserSettings } from "redux-share/slices/userSlice";
import { createSettings } from "redux-share/actions/user.actions";
import { deActiveSettingLog, sendFluentLog } from "../../../../../logs";
import { handlePopoverSizeInMiniPlayer } from "helpers/helpers";
import { shallowEqual } from 'react-redux';
import ControllbarTooltip from "../../mainComponent/footer/controllbar/controllbarTooltip";
import ChevronLeftIcon from "../../icon-components/chevronLeft";
import Toggle from "../toggle/toggle";
import QualityLevels from "../qualityLevels/qualityLevels";
import MultiAudio from "../multiAudio/multiAudio";
import SubtitlesMenu from "../subtitles/subtitlesMenu";

const Settings = () => {
    const dispatch = useAppDispatch();
    const { multiAudio, selectedAudioId, selectedSubtitle } = useAppSelector(({ videoData: { multiAudio, selectedAudioId, selectedSubtitle, params, videoElementSizeInfo } }) => ({ multiAudio, selectedAudioId, selectedSubtitle, params, videoElementSizeInfo }), shallowEqual);
    const { settingPopover } = useAppSelector(({ popovers: { settingPopover } }) => ({ settingPopover }), shallowEqual);
    const { settings } = useAppSelector(({ user: { settings } }) => ({ settings }), shallowEqual);
    const { qualityLevels, qualityBitrates, manualQuality, autoQuality, showMiniPlayer } = useAppSelector(({ controller: { qualityLevels, qualityBitrates, manualQuality, autoQuality, showMiniPlayer } }) => ({ qualityLevels, qualityBitrates, manualQuality, autoQuality, showMiniPlayer }), shallowEqual);

    const [section, setSection] = useState<string>("main");
    const [elementHeight, setElementHeight] = useState<number>(0);
    const [showTooltip, setShowTooltip] = useState<boolean>(false);

    const settingsRef = useRef(null);

    useOnClickOutside(settingsRef, () => {
        dispatch(setSettingPopover(false))
        setTimeout(() => setSection("main"), 500)
    });

    useEffect(() => {
        if (conditions(ConditionName.isMiniPlayer) || conditions(ConditionName.showMiniPlayer)) setSection("qualityLevels")
        setElementHeight(document.getElementById(section)?.offsetHeight!);
    }, [section, showMiniPlayer])

    const handleQualityLabel = () => {
        const quality = conditions(ConditionName.isAutoQuality) ? Number(qualityLevels[autoQuality!]) : Number(qualityLevels[manualQuality !== undefined ? manualQuality : settings.quality])
        if (quality >= 720 && quality < 1080) return "HD";
        else if (quality >= 1080 && quality < 2160) return "FHD";
        else if (quality >= 2160 && quality < 4320) return "4K";
        else if (quality >= 4320) return "8K";
        else return `${quality}p`;
    }

    const handleToggle = () => {
        dispatch(setUserSettings({ ...settings, poll_emoji: !settings.poll_emoji }));
        createSettings({ ...settings, poll_emoji: !settings.poll_emoji });
        sendFluentLog(deActiveSettingLog());
    }

    const qualityTitle = () => {
        let result: string;
        if (conditions(ConditionName.isAutoQuality)) result = `خودکار (${qualityLevels[autoQuality!]}P - ${qualityBitrates[autoQuality!]})`
        else if (Number(qualityLevels[manualQuality!]) === justAudio) result = "فقط صدا";
        else result = `${qualityLevels[manualQuality!]}P - ${qualityBitrates[manualQuality!]}`;
        return result;
    }

    const handleShowSettingPopover = () => {
        if (conditions(ConditionName.isComplexPortrait)) dispatch(setClearPopoversSlice());
        dispatch(setSettingPopover(!settingPopover));
    }

    return (
        <div className={`pl-inline-flex ${conditions(ConditionName.isLandscape) && "pl-relative"}`} ref={settingsRef}>
            <div
                className={`pl-flex pl-items-center pl-relative pl-cursor-pointer`}
                onClick={handleShowSettingPopover}
                onMouseOver={() => !conditions(ConditionName.isTouchScreen) && setShowTooltip(true)}
                onMouseLeave={() => !conditions(ConditionName.isTouchScreen) && setShowTooltip(false)}
            >
                <SettingsIcon />
                {!conditions(ConditionName.isMiniPlayer) && <span className={`${Number(qualityLevels[conditions(ConditionName.isAutoQuality) ? autoQuality! : manualQuality !== undefined ? manualQuality : settings.quality]) >= 720 ? "pl-bg-red-light pl-right-[-6px]" : "pl-bg-secondary-500 pl-right-[-10px]"} pl-absolute pl-top-0 ${conditions(ConditionName.isComplexPortrait) && "pl-top-[-4px] pl-right-[-16px]"} pl-text-[8px] pl-text-white pl-px-1 pl-pt-[1px]`}>{handleQualityLabel()}</span>}
                {showTooltip && <ControllbarTooltip title="تنظیمات" />}
            </div>
            <Popover
                classNames={`!pl-p-0 pl-overflow-hidden ${!conditions(ConditionName.isComplexPortrait) && "pl-mb-[33px] pl-right-0"} ${conditions(ConditionName.isMiniPlayer) && !conditions(ConditionName.isFullMobilePortrait) ? `!pl-fixed pl-rounded-r-none pl-w-[200px] !pl-mb-0` : "pl-w-[285px]"}`}
                animation={conditions(ConditionName.isMiniPlayer) && !conditions(ConditionName.isFullMobilePortrait) ? (settingPopover ? "animate__fadeInRight" : "animate__fadeOutRight") : undefined}
                styles={{ maxHeight: `${elementHeight}px`, ...handlePopoverSizeInMiniPlayer() }}
                status={settingPopover}
                onClose={() => dispatch(setSettingPopover(false))}
                type={conditions(ConditionName.isComplexPortrait) ? "modal" : "popover"}
                isBlurBackground={conditions(ConditionName.isComplexPortrait)}
                ref={settingsRef}
            >
                <>
                    {section === "main" && (
                        <div id="main" className="pl-px-4 pl-rounded-[10px] pl-flex pl-flex-col pl-gap-[14px] pl-py-3 animate__animated animate__fadeIn">
                            {!conditions(ConditionName.isStaticFormat) && (
                                <>
                                    <div
                                        className="pl-flex pl-justify-between pl-items-center pl-border-b pl-pb-[14px] pl-cursor-pointer pl-text-[14px]"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setSection("qualityLevels")
                                        }}
                                    >
                                        <span className="pl-font-medium">کیفیت</span>
                                        <span className="pl-font-bold">{qualityTitle()}</span>
                                    </div>
                                    {conditions(ConditionName.hasSubtitle) && (
                                        <div
                                            className="pl-flex pl-justify-between pl-items-center pl-border-b pl-pb-[14px] pl-cursor-pointer pl-text-[14px]"
                                            onClick={() => setSection("subtitles")}
                                        >
                                            <span className="pl-font-medium">انتخاب زیرنویس</span>
                                            <span className="pl-font-bold">{conditions(ConditionName.isSelectedSubtitle) ? selectedSubtitle.label : "بدون زیرنویس"}</span>
                                        </div>
                                    )}
                                    {conditions(ConditionName.hasMultiAudio) && (
                                        <div
                                            className="pl-flex pl-justify-between pl-items-center pl-border-b pl-pb-[14px] pl-cursor-pointer pl-text-[14px]"
                                            onClick={() => setSection("multiAudio")}
                                        >
                                            <span className="pl-font-medium">انتخاب زبان</span>
                                            <span className="pl-font-bold">{multiAudio.find((audio: any, index: number) => index === selectedAudioId).name}</span>
                                        </div>
                                    )}
                                    {conditions(ConditionName.showShareInSetting) && (
                                        <div
                                            className="pl-flex pl-justify-between pl-items-center pl-border-b pl-pb-3 pl-cursor-pointer pl-text-[14px]"
                                            onClick={() => dispatch(setSharePopover(true))}
                                        >
                                            <span className="pl-font-medium">اشتراک گذاری</span>
                                            <ChevronLeftIcon className="pl-stroke-gray-800 dark:pl-stroke-dark-5 pl-w-3 pl-h-3" />
                                        </div>
                                    )}
                                </>
                            )}

                            {conditions(ConditionName.isLiveParam) && (
                                <div className="pl-flex pl-justify-between pl-items-center">
                                    <span className="pl-text-[14px] pl-font-medium pl-ml-10">نمايش نظرسنجى و ايموجى ها</span>
                                    <Toggle enabled={settings.poll_emoji} setEnabled={handleToggle} />
                                </div>
                            )}
                        </div>
                    )}
                    {section === "qualityLevels" && !conditions(ConditionName.isStaticFormat) && <QualityLevels setSection={setSection} />}
                    {section === "multiAudio" && <MultiAudio setSection={setSection} />}
                    {section === "subtitles" && <SubtitlesMenu setSection={setSection} />}
                </>
            </Popover>
        </div>
    )
}

export default memo(Settings);